import * as FileSystem from 'expo-file-system';

/**
 * Persistent audio cache for trail step audio.
 *
 * - Files live under `documentDirectory + 'trail-audio/'` so the OS does NOT
 *   evict them under storage pressure (unlike `cacheDirectory`). This means
 *   re-opening any previously played step is zero-network forever, until the
 *   user reinstalls the app.
 * - In-flight downloads are deduped via an in-memory promise map. If two
 *   callers race (e.g. background prefetch from step page mount + user tap),
 *   they await the same download instead of starting two.
 * - Cache key strips query params (Supabase signed URLs change per request
 *   but the underlying file is the same).
 *
 * Storage budget: ~5-10MB per audio file × 12 steps × N trails. For the
 * default 12 Passos trail this is ≤ 120MB worst case. Acceptable.
 *
 * Used by:
 * - `app/trail/[slug]/[stepNumber].tsx` — fires `prefetchAudioInBackground`
 *   on step mount for the current step + step N+1 (Plus users only).
 * - `contexts/AudioPlayerContext.tsx` — `playTrack` awaits `prefetchAudio`
 *   so the player always uses the local file when available.
 */

const AUDIO_CACHE_DIR = `${FileSystem.documentDirectory}trail-audio/`;
const inFlight = new Map<string, Promise<string>>();
let dirEnsured = false;

/**
 * Canonical cache key for a remote audio URL. Strips query params so that
 * Supabase signed URLs (whose tokens rotate per request but point to the same
 * underlying file) share a single cache entry AND a single dedupe slot.
 * Both `urlToFilename` and the in-flight Map MUST key off this — otherwise
 * two callers with differently-signed URLs to the same file would race-
 * download and race-write to the same localPath.
 */
function canonicalCacheKey(url: string): string {
  return url.split('?')[0];
}

function urlToFilename(url: string): string {
  const clean = canonicalCacheKey(url);
  // Take last 2 path segments + sanitize for filesystem safety.
  const segments = clean.split('/').slice(-2).join('_');
  const safe = segments.replace(/[^a-zA-Z0-9._-]/g, '_');
  return safe.length > 100 ? safe.slice(-100) : safe;
}

async function ensureDir(): Promise<void> {
  if (dirEnsured) return;
  try {
    const info = await FileSystem.getInfoAsync(AUDIO_CACHE_DIR);
    if (!info.exists) {
      await FileSystem.makeDirectoryAsync(AUDIO_CACHE_DIR, { intermediates: true });
    }
    dirEnsured = true;
  } catch (err) {
    console.warn('⚠️ [AudioPrefetch] ensureDir failed:', err);
  }
}

/**
 * Returns a local file:// URI for the given remote audio URL. If the file is
 * already cached on disk, returns immediately. If a download is in flight for
 * the same URL, awaits it. Otherwise starts a fresh download.
 *
 * Throws on network/disk failure — callers should fall back to the remote URL
 * so playback still works in the failure case.
 */
export async function prefetchAudio(url: string | null | undefined): Promise<string> {
  if (!url) {
    throw new Error('prefetchAudio: url is required');
  }
  // Dedupe by the canonical cache key (NOT raw URL) so that concurrent
  // callers passing differently-signed Supabase URLs to the same file await
  // a single download instead of racing two.
  const cacheKey = canonicalCacheKey(url);
  const existing = inFlight.get(cacheKey);
  if (existing) return existing;

  const promise = (async () => {
    await ensureDir();
    const localPath = `${AUDIO_CACHE_DIR}${urlToFilename(url)}`;

    try {
      const info = await FileSystem.getInfoAsync(localPath);
      if (info.exists && info.size && info.size > 0) {
        console.log('🎵 [AudioPrefetch] Cache hit:', localPath);
        return localPath;
      }
    } catch {
      // Disk read failed — fall through and attempt re-download.
    }

    console.log('🎵 [AudioPrefetch] Downloading:', url.split('?')[0]);
    const start = Date.now();
    const result = await FileSystem.downloadAsync(url, localPath);
    console.log(`✅ [AudioPrefetch] Downloaded in ${Date.now() - start}ms (${result.uri})`);
    return result.uri;
  })();

  inFlight.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    // Always remove on completion (success OR failure). On success the next
    // call hits the disk cache check above; on failure the next call retries
    // the download.
    inFlight.delete(cacheKey);
  }
}

/**
 * Fire-and-forget helper for the page-mount prefetch path. Swallows errors
 * so a failed background prefetch never crashes the screen — `playTrack` will
 * fall back to the remote URL if the file isn't there at tap time.
 */
export function prefetchAudioInBackground(url: string | null | undefined): void {
  if (!url) return;
  prefetchAudio(url).catch((err) => {
    console.warn('⚠️ [AudioPrefetch] Background prefetch failed:', err);
  });
}
